import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { useSiteConfigStore } from './siteConfig'
import { useSite } from '~/composables/core/useSite'

export const useMainStore = defineStore('main', () => {
  const siteConfigStore = useSiteConfigStore()

  // State (使用 ref)
  const loading = ref({})
  const downloadGuide = ref(false)
  const modalLoading = ref(false)
  const loadingGame = ref(false)
  const modal = ref(null)
  const modalParams = ref(null)
  const systemModal = ref(null)
  const systemModalParams = ref(null)
  const verifyModal = ref(null)
  const bindBankStrategy = ref(null)
  const bindBankCheckpoint = ref(null)
  const showBindBank = ref(false)
  const bindBankModal = ref(null)

  // Promise resolver（用於等待銀行綁定完成）
  let bindBankResolver = null
  const errorMessage = ref(null)
  const errorModal = ref(null)
  const animationInitData = ref(null)

  // Global info
  const jackpotList = ref([])
  const affiliateCode = ref(null)
  const friendCode = ref(null)
  const turnoverLimitTip = ref(false)
  const withdrawRule = ref({})
  const promotion = ref(null)
  const browserAgent = ref(null)
  const navigator = ref({})
  const trackLocation = ref(null)
  const storage = ref(null)
  const customerServiceModal = ref(null)
  const playingGame = ref(null)
  const jackpotWinning = ref({})
  const toast = ref([])
  const gameSearchPageEnable = ref(false)
  const connectSocket = ref(0)
  const providerInfo = ref([])
  const freeGameList = ref([])
  const telegramUserData = ref(null)
  const additionalUserInfo = ref(null)
  const updateMessage = ref(false)
  const normalBroadcast = ref(null)
  const shareGame = ref(null)

  // Getters (使用 computed)
  const getFreeGameList = computed(() => freeGameList.value)
  const getProviderInfo = computed(() => providerInfo.value)

  const isWebView = computed(() => {
    // App
    return browserAgent.value?.ua?.includes('th1games')
  })

  const isAndroid = computed(() => {
    // Android
    return navigator.value.userAgent?.indexOf('Android') > -1 || navigator.value.userAgent?.indexOf('Adr') > -1
  })

  const isIOS = computed(() => {
    return browserAgent.value?.os?.name === 'iOS'
  })

  const isInStandaloneMode = computed(() => {
    // Standalone PWA
    return 'standalone' in navigator.value && navigator.value.standalone
  })

  const downloadVisible = computed(() => {
    // 不是 webview + 不是 PWA + 是 Android 或 iOS
    return !isWebView.value && !isInStandaloneMode.value && (isAndroid.value || isIOS.value)
  })

  const isTablet = computed(() => {
    const userAgent = navigator.value.userAgent.toLowerCase()
    const isAndroidTablet = /android/.test(userAgent) && !/mobile/.test(userAgent)
    const isIPad = /ipad/.test(userAgent)

    return isAndroidTablet || isIPad
  })

  const isDesktop = computed(() => {
    // 不是 webview + 不是 PWA + 也不是 Android 或 iOS
    // 全部都不是的話就是 Desktop,可以使用 dragscroll
    return !isWebView.value && !isInStandaloneMode.value && !isAndroid.value && !isIOS.value
  })

  const iOSversion = computed(() => {
    return browserAgent.value?.os?.version
  })

  const getDownloadLink = computed(() => {
    const { defaultChannel, defaultSubChannel } = useRuntimeConfig().public
    const parameterObj = {
      ac: affiliateCode.value,
      fc: friendCode.value,
      pix: storage.value?.get('pix'),
      kpix: storage.value?.get('kpix'),
      channel: storage.value?.get('channel') || defaultChannel,
      subchannel: storage.value?.get('subchannel') || defaultSubChannel,
      feature: storage.value?.get('subchannel') || defaultSubChannel,
    }
    return siteConfigStore.getDownloadLink(parameterObj)
  })

  const getLoading = computed(() => {
    return Object.values(loading.value).some((v) => v)
  })

  const getModalLoading = computed(() => modalLoading.value)
  const getLoadingGame = computed(() => loadingGame.value)
  const getModal = computed(() => modal.value)
  const getModalParams = computed(() => modalParams.value)
  const getShareGame = computed(() => shareGame.value)
  const getSystemModal = computed(() => systemModal.value)
  const getSystemModalParams = computed(() => systemModalParams.value)
  const getVerifyModal = computed(() => verifyModal.value)
  const getBindBankStrategy = computed(() => bindBankStrategy.value)
  const getBindBankCheckpoint = computed(() => bindBankCheckpoint.value)
  const getShowBindBank = computed(() => showBindBank.value)
  const getBindBankModal = computed(() => bindBankModal.value)
  const getErrorMessage = computed(() => errorMessage.value)
  const getErrorModal = computed(() => errorModal.value)
  const getAnimationInitData = computed(() => animationInitData.value)
  const getTrackLocation = computed(() => trackLocation.value)

  const getJackpotList = computed(() => jackpotList.value || [])

  const isGuiding = computed(() => downloadGuide.value)
  const getFriendCode = computed(() => friendCode.value)
  const getTurnoverLimitTip = computed(() => turnoverLimitTip.value)
  const getWithdrawRule = computed(() => withdrawRule.value)
  const getPromotion = computed(() => promotion.value)

  const isBRLCurrency = computed(() => {
    return (process.env.CURRENCY_SYMBOL || '') === 'R$'
  })

  const getCustomerServiceModal = computed(() => customerServiceModal.value)
  const getPlayingGame = computed(() => playingGame.value)
  const getJackpotWinning = computed(() => jackpotWinning.value)
  const getToast = computed(() => toast.value)
  const getGameSearchPageEnable = computed(() => gameSearchPageEnable.value)

  const getConnectSocket = computed(() => {
    // 0: 未連線, 1: 連線中, 2: 連線成功
    return connectSocket.value === 2
  })

  const getTelegramUserData = computed(() => telegramUserData.value)
  const getAdditionalUserInfo = computed(() => additionalUserInfo.value)
  const getUpdateMessage = computed(() => updateMessage.value)
  const getNormalBroadcast = computed(() => normalBroadcast.value)

  // Actions (使用普通函數)
  function setFreeGameList(data) {
    freeGameList.value = data
  }

  function setProviderInfo(data) {
    providerInfo.value = data
  }

  function setDownloadGuide(data) {
    downloadGuide.value = data
  }

  function setLoading(data) {
    loading.value = { ...loading.value, ...data }
  }

  function setModalLoading(data) {
    modalLoading.value = data
  }

  function setLoadingGame(data) {
    loadingGame.value = data
  }

  function setModal(data) {
    if (modal.value && data) {
      console.warn(`[Modal] ${modal.value} -> ${data}`)
    }

    modal.value = data
    if (data === null || data === undefined) {
      modalParams.value = null
    }
  }

  function setModalParams(data) {
    modalParams.value = data
  }

  function setShareGame(data) {
    shareGame.value = data
  }

  function setSystemModal(data) {
    systemModal.value = data
    if (data === null || data === undefined) {
      systemModalParams.value = null
    }
  }

  function setSystemModalParams(data) {
    systemModalParams.value = data
  }

  function closeModal(data) {
    if (data === modal.value) {
      modal.value = null
    }
  }

  function setVerifyModal(data) {
    verifyModal.value = data
  }

  function setBindBankStrategy(data) {
    bindBankStrategy.value = data
  }

  function setBindBankCheckpoint(data) {
    bindBankCheckpoint.value = data
  }

  function setShowBindBank(data) {
    showBindBank.value = data
  }

  function setBindBankModal(data) {
    bindBankModal.value = data
  }

  /**
   * 設定銀行綁定 Promise resolver
   */
  function setBindBankResolver(resolver) {
    bindBankResolver = resolver
  }

  /**
   * 解決銀行綁定 Promise
   * @param {boolean} success - 綁定是否成功
   */
  function resolveBindBank(success) {
    if (bindBankResolver) {
      bindBankResolver(success)
      bindBankResolver = null
    }
  }

  function setErrorMessage(data) {
    errorMessage.value = data
  }

  function setErrorModal(data) {
    errorModal.value = data
  }

  function setAnimationInitData(data) {
    animationInitData.value = data
  }

  function setJackpotList(data) {
    jackpotList.value = data
  }

  function setFriendCode(data) {
    const { code } = data
    friendCode.value = code
  }

  const setTurnoverLimitTip = (data) => {
    turnoverLimitTip.value = data
  }

  function setWithdrawRule(data) {
    withdrawRule.value = data
  }

  function setPromotion(data) {
    promotion.value = data
  }

  function setBrowserAgent(data) {
    browserAgent.value = data
  }

  function setNavigator(data) {
    navigator.value = data
  }

  function setTrackLocation(data) {
    trackLocation.value = data
  }

  function setStorage(data) {
    storage.value = data
  }

  function setCustomrerServiceModal(data) {
    customerServiceModal.value = data
  }

  function setPlayingGame(data) {
    playingGame.value = data
  }

  function setJackpotWinning(data) {
    jackpotWinning.value = data
  }

  function setToast(data) {
    toast.value = data
  }

  function pushToast(data) {
    toast.value = [...toast.value, data]
  }

  function setGameSearchPageEnable(data) {
    gameSearchPageEnable.value = data
  }

  function setConnectSocket(data) {
    connectSocket.value = data
  }

  function setTelegramUserData(data) {
    telegramUserData.value = data
  }

  function setAdditionalUserInfo(data) {
    additionalUserInfo.value = data
  }

  function setUpdateMessage(data) {
    updateMessage.value = data
  }

  function setNormalBroadcast(data) {
    normalBroadcast.value = data
  }

  // Return everything to make it available
  return {
    // State
    loading,
    downloadGuide,
    modalLoading,
    loadingGame,
    modal,
    modalParams,
    systemModal,
    systemModalParams,
    verifyModal,
    bindBankStrategy,
    bindBankCheckpoint,
    showBindBank,
    bindBankModal,
    errorMessage,
    errorModal,
    animationInitData,
    jackpotList,
    affiliateCode,
    friendCode,
    turnoverLimitTip,
    withdrawRule,
    promotion,
    browserAgent,
    navigator,
    trackLocation,
    storage,
    customerServiceModal,
    playingGame,
    jackpotWinning,
    toast,
    gameSearchPageEnable,
    connectSocket,
    providerInfo,
    freeGameList,
    telegramUserData,
    additionalUserInfo,
    updateMessage,
    normalBroadcast,
    shareGame,

    // Getters
    getFreeGameList,
    getProviderInfo,
    isWebView,
    isAndroid,
    isIOS,
    isInStandaloneMode,
    downloadVisible,
    isTablet,
    isDesktop,
    iOSversion,
    getDownloadLink,
    getLoading,
    getModalLoading,
    getLoadingGame,
    getModal,
    getModalParams,
    getShareGame,
    getSystemModal,
    getSystemModalParams,
    getVerifyModal,
    getBindBankStrategy,
    getBindBankCheckpoint,
    getShowBindBank,
    getBindBankModal,
    getErrorMessage,
    getErrorModal,
    getAnimationInitData,
    getTrackLocation,
    getJackpotList,
    isGuiding,
    getFriendCode,
    getTurnoverLimitTip,
    getWithdrawRule,
    getPromotion,
    isBRLCurrency,
    getCustomerServiceModal,
    getPlayingGame,
    getJackpotWinning,
    getToast,
    getGameSearchPageEnable,
    getConnectSocket,
    getTelegramUserData,
    getAdditionalUserInfo,
    getUpdateMessage,
    getNormalBroadcast,

    // Actions
    setFreeGameList,
    setProviderInfo,
    setDownloadGuide,
    setLoading,
    setModalLoading,
    setLoadingGame,
    setModal,
    setModalParams,
    setShareGame,
    setSystemModal,
    setSystemModalParams,
    closeModal,
    setVerifyModal,
    setBindBankStrategy,
    setBindBankCheckpoint,
    setShowBindBank,
    setBindBankModal,
    setBindBankResolver,
    resolveBindBank,
    setErrorMessage,
    setErrorModal,
    setAnimationInitData,
    setJackpotList,
    setFriendCode,
    setTurnoverLimitTip,
    setWithdrawRule,
    setPromotion,
    setBrowserAgent,
    setNavigator,
    setTrackLocation,
    setStorage,
    setCustomrerServiceModal,
    setPlayingGame,
    setJackpotWinning,
    setToast,
    pushToast,
    setGameSearchPageEnable,
    setConnectSocket,
    setTelegramUserData,
    setAdditionalUserInfo,
    setUpdateMessage,
    setNormalBroadcast,
  }
})
